import { composerStageForAnchor } from '@/lib/care-plans/composer-stage-routing';
import type { CarePlanStageId } from '@/lib/client-care-redesign/care-plan-stage-api';
import {
  isGenericListReturnHref,
  returnLabelForHref,
  safeReturnHref,
} from '@/lib/navigation/safe-return-href';

export type ClientCarePlanReturnContext = {
  clientId: string;
  returnTab?: string;
  returnHash?: string;
};

export type NextSearchParamRecord = Record<string, string | string[] | undefined>;

export function nextSearchParamsToUrlSearchParams(input: NextSearchParamRecord): URLSearchParams {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(input)) {
    if (Array.isArray(value)) value.forEach((entry) => params.append(key, entry));
    else if (value !== undefined) params.set(key, value);
  }
  return params;
}

const CLIENT_ALIAS_CONTEXT_KEYS_TO_REPLACE = new Set([
  'tab',
  'from',
  'clientId',
  'returnTo',
  'returnTab',
  'returnHash',
]);

/** Retains benign view/filter context while replacing identity and return-routing keys. */
export function clientRecordAliasHref(
  clientId: string,
  incoming: URLSearchParams,
  options: { tab: string; hash?: string },
): string {
  const params = new URLSearchParams();
  for (const [key, value] of incoming) {
    if (!CLIENT_ALIAS_CONTEXT_KEYS_TO_REPLACE.has(key)) params.append(key, value);
  }
  params.set('tab', options.tab);
  if (options.hash) params.set('panel', options.hash);
  const hash = options.hash ? `#${options.hash}` : '';
  return `/clients/${clientId}?${params.toString()}${hash}`;
}

export function clientCarePlanReturnParams(context: ClientCarePlanReturnContext): URLSearchParams {
  const params = new URLSearchParams();
  params.set('from', 'client');
  params.set('clientId', context.clientId);
  params.set('returnTab', context.returnTab ?? 'sah-hub');
  if (context.returnHash) params.set('returnHash', context.returnHash);
  return params;
}

export function carePlanComposeHref(
  carePlanId: string,
  options?: {
    mode?: 'standard' | 'amendment';
    hash?: string;
    stage?: CarePlanStageId;
    returnContext?: ClientCarePlanReturnContext;
    addServiceCode?: string;
    frequencyPerQuarter?: string | number;
    unitCost?: string | number;
    reviewId?: string;
  },
): string {
  const params = new URLSearchParams();
  if (options?.mode && options.mode !== 'standard') {
    params.set('mode', options.mode);
  }
  if (options?.reviewId) params.set('reviewId', options.reviewId);
  if (options?.returnContext) {
    for (const [key, value] of clientCarePlanReturnParams(options.returnContext)) {
      params.set(key, value);
    }
  }
  if (options?.addServiceCode) params.set('addServiceCode', options.addServiceCode);
  if (options?.frequencyPerQuarter != null) {
    params.set('frequencyPerQuarter', String(options.frequencyPerQuarter));
  }
  if (options?.unitCost != null) params.set('unitCost', String(options.unitCost));
  const hash = options?.hash ? options.hash.replace(/^#/, '') : '';
  const stage = options?.stage ?? (hash ? composerStageForAnchor(hash) : null);
  if (stage) params.set('stage', stage);
  const query = params.toString();
  const hashSuffix = hash ? `#${hash}` : '';
  return `/care-plans/${carePlanId}/compose${query ? `?${query}` : ''}${hashSuffix}`;
}

export function carePlanDetailHref(
  carePlanId: string,
  options?: {
    hash?: string;
    returnContext?: ClientCarePlanReturnContext;
  },
): string {
  const params = new URLSearchParams();
  if (options?.returnContext) {
    for (const [key, value] of clientCarePlanReturnParams(options.returnContext)) {
      params.set(key, value);
    }
  }
  const query = params.toString();
  const hash = options?.hash ? `#${options.hash}` : '';
  return `/care-plans/${carePlanId}${query ? `?${query}` : ''}${hash}`;
}

export function carePlanGoalHref(
  carePlanId: string,
  goalId: string,
  options?: { returnContext?: ClientCarePlanReturnContext },
): string {
  const params = options?.returnContext
    ? clientCarePlanReturnParams(options.returnContext)
    : new URLSearchParams();
  const query = params.toString();
  return `/care-plans/${carePlanId}/goals/${goalId}${query ? `?${query}` : ''}`;
}

export function carePlanSynthesisHref(options?: {
  clientId?: string | null;
  returnContext?: ClientCarePlanReturnContext;
  /** Completed assessments to pre-select in the synthesis workstation. */
  assessmentIds?: readonly string[];
}): string {
  const params = new URLSearchParams();
  if (options?.returnContext) {
    for (const [key, value] of clientCarePlanReturnParams(options.returnContext)) {
      params.set(key, value);
    }
  } else if (options?.clientId) {
    params.set('clientId', options.clientId);
  }
  if (options?.clientId && !params.has('clientId')) {
    params.set('clientId', options.clientId);
  }
  for (const assessmentId of options?.assessmentIds ?? []) {
    if (assessmentId) params.append('assessmentIds', assessmentId);
  }
  const query = params.toString();
  return `/care-plans/synthesize${query ? `?${query}` : ''}`;
}

export function clientJourneyHref(
  clientId: string,
  options?: { tab?: string; hash?: string },
): string {
  const tab = options?.tab ?? 'sah-hub';
  // The hash alone is unreliable across in-place tab switches (the URL-sync
  // replaceState can race the browser applying it — 2026-07-07 blocker-CTA
  // audit). Mirror it as ?panel= so the destination can deterministically
  // open + scroll the target; keep the hash for native same-page behaviour.
  const panel = options?.hash ? `&panel=${encodeURIComponent(options.hash)}` : '';
  const hash = options?.hash ? `#${options.hash}` : '';
  return `/clients/${clientId}?tab=${tab}${panel}${hash}`;
}

const CLIENT_BUDGET_ANCHORS = new Set([
  'funding',
  'funding-setup',
  'funding-authorisations',
  'forecast',
  'plan-fit',
  'contributions',
  'envelopes',
  'cap-supplements',
  'hcp-opening',
  'cm-time-admin',
  'sah-finance-handoff',
  'reconciliation',
  'exceptions',
]);

export function resolveClientScopedHref(clientId: string, href: string): string {
  if (!href) return href;
  if (href.startsWith('#')) {
    const anchor = href.slice(1).split(/[?#]/)[0] ?? '';
    const tab = CLIENT_BUDGET_ANCHORS.has(anchor) ? 'budget' : 'sah-hub';
    return clientJourneyHref(clientId, { tab, hash: anchor });
  }
  if (href.startsWith('?')) return `/clients/${clientId}${href}`;
  return href;
}

export function resolveClientCarePlanBackHref(
  searchParams: URLSearchParams,
  carePlanId: string,
  clientId?: string | null,
): { href: string; label: string } {
  const explicitReturnTo = safeReturnHref(searchParams.get('returnTo'), {
    expectedClientId: clientId,
  });
  if (explicitReturnTo && !(clientId && isGenericListReturnHref(explicitReturnTo))) {
    return {
      href: explicitReturnTo,
      label: returnLabelForHref(explicitReturnTo),
    };
  }

  const returnContext = clientCarePlanReturnContextFromSearchParams(searchParams);
  if (returnContext && (!clientId || returnContext.clientId === clientId)) {
    const returnTab = returnContext.returnTab ?? 'sah-hub';
    const returnHash = returnContext.returnHash ?? (returnTab === 'budget' ? 'funding' : 'today');
    const label =
      returnTab === 'budget'
        ? 'Back to client Budget'
        : returnTab === 'care-plans'
          ? 'Back to client care plans'
          : 'Back to client Journey';
    return {
      href: clientJourneyHref(returnContext.clientId, {
        tab: returnTab,
        hash: returnHash,
      }),
      label,
    };
  }
  const queryClientId = searchParams.get('clientId');
  if (queryClientId && (!clientId || queryClientId === clientId)) {
    const returnTab = searchParams.get('returnTab') ?? 'sah-hub';
    const returnHash =
      searchParams.get('returnHash') ?? (returnTab === 'budget' ? 'funding' : 'today');
    const label =
      returnTab === 'budget'
        ? 'Back to client Budget'
        : returnTab === 'care-plans'
          ? 'Back to client care plans'
          : 'Back to client Journey';
    return {
      href: clientJourneyHref(queryClientId, {
        tab: returnTab,
        hash: returnHash,
      }),
      label,
    };
  }
  // No explicit return context (e.g. deep-linked, or opened from a surface that
  // did not carry one). Fall back to the plan's sensible parent rather than the
  // plan's own page — for a client-scoped plan that is the client's Care Plans
  // tab; otherwise the care-plans list.
  if (clientId) {
    return {
      href: clientJourneyHref(clientId, { tab: 'care-plans' }),
      label: 'Back to client care plans',
    };
  }
  return {
    href: `/care-plans/${carePlanId}`,
    label: 'Back to this care plan',
  };
}

export function safeClientReturnHref(
  value: string | null,
  expectedClientId?: string | null,
): string | null {
  const href = safeReturnHref(value, { expectedClientId });
  if (!href?.startsWith('/clients/')) return null;
  return href;
}

/** Roster pages that may hand a client into recurring schedules and expect a way back. */
const SCHEDULE_ROSTER_RETURNS: Readonly<Record<string, string>> = {
  '/roster/continuity': 'Back to Client continuity',
};

/**
 * Return link for the recurring schedules pages: the client Delivery tab
 * (existing behaviour) or an allow-listed roster origin such as Client
 * continuity. Anything else is ignored.
 */
export function scheduleReturnLink(
  value: string | null,
  expectedClientId?: string | null,
): { href: string; label: string } | null {
  const clientHref = safeClientReturnHref(value, expectedClientId);
  if (clientHref) return { href: clientHref, label: 'Back to client Delivery' };
  const label = value ? SCHEDULE_ROSTER_RETURNS[value] : undefined;
  return label && value ? { href: value, label } : null;
}

export function clientCarePlanReturnContextFromSearchParams(
  searchParams: URLSearchParams,
): ClientCarePlanReturnContext | undefined {
  const from = searchParams.get('from');
  const clientId = searchParams.get('clientId');
  if (from !== 'client' || !clientId) return undefined;
  return {
    clientId,
    returnTab: searchParams.get('returnTab') ?? 'sah-hub',
    returnHash: searchParams.get('returnHash') ?? undefined,
  };
}
